import { Fragment, useCallback, useEffect, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { Icon, type IconName } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import {
  createDossier,
  finalizeDossier,
  generateDossier,
  getDossier,
  getDossierArtifact,
  listCaseDossiers,
  listDossierComponents,
  previewDossierComponent,
} from '../../api/dossier'
import { ApiError } from '../../api/problem'
import { useCaseContexts } from '../tasks/useCaseContexts'
import type { CaseView } from '../../types/case'
import type {
  DossierComponentPreviewView,
  DossierComponentView,
  DossierSummaryView,
  DossierView,
} from '../../types/dossier'

/**
 * The Film Dossier tab (IB-159) — the checklist + Generate/Finalize/Download surface for
 * `module.dossier`'s `kind=client_deliverable` deliverable (IB-21, merged). Reads/writes the real
 * backend contract (`DossierController`/`DossierService`) — no mock, no stubbed data.
 *
 * <h2>Disclosed gaps and client-side-only policies (read before changing this file)</h2>
 *
 * 1. **RESOLVED: `GET /cases/{caseId}/dossiers` now exists.** This screen previously had no way to
 *    resolve "the open draft/latest Dossier for this Case" other than a `sessionStorage` cache
 *    written by its own {@link handleStartDossier} click, honestly surfacing a `'createConflict'`
 *    dead-end in a fresh session/tab that already had an open draft. `DossierController` now
 *    exposes `GET /api/v1/cases/{caseId}/dossiers` (`DossierSummaryView[]`, newest-version-first,
 *    RLS + `VisibilitySpecification`-scoped) — this screen calls it as the SOLE source of truth on
 *    mount and after every create/generate/finalize action; `sessionStorage` and the
 *    `'createConflict'` phase are gone. The "active" Dossier this screen drives the
 *    checklist/Generate/Finalize/Download flow for is: the open `draft` if one exists, else the
 *    newest version (a `final`) — so a second `POST .../dossiers` after every version is final
 *    starts a genuinely new one ("Start new version"), while a stray 409 race (two tabs creating
 *    at once) is recovered by simply re-fetching the list, never a dead end. Linkage note: a
 *    `Dossier` references its Case directly (`dossier.case_id`, no `ProgramFiling` linkage) — this
 *    is a Case-level list, not a per-filing one (confirmed against `Dossier.java`/V22).
 * 2. **"Finalize enabled only when every component is generated-or-stub" is a CLIENT-SIDE-ONLY
 *    gate.** The real `DossierAssemblyEngine.finalizeDossier` does NOT reject a finalize because a
 *    component is still `pending` — it embeds `PendingPlaceholderRenderer`'s honest placeholder for
 *    that component and finalizes anyway (component 5's stub is `pending` on every pass by design,
 *    and is never alone a blocker either way). This screen still disables the button per the story
 *    ACs / UX pass (`docs/prototypes/film-prototype.html`) — a real user preview of "not everything
 *    is ready yet" — but that is advisory, not an enforced precondition; the backend's own
 *    authoritative behaviour differs and is not being changed here.
 * 3. **No per-component `note`/reason text exists on the wire.** `DossierComponentView` carries no
 *    free-text field — the specific "blocked because…" narrative the prototype mocks is prototype
 *    flavor text, not a real field. `componentDetail` below derives a short, honest, generic
 *    description from the real `status`/`source` fields instead of fabricating that copy.
 * 4. **No capability gate on Create/Generate/Finalize/Download.** `DossierManageGuard` is a
 *    declared `*Stub` (permit-all/deny-all by profile, TODO M3) with no `/me/ui-config` component
 *    key registered yet (unlike `ProductionDetailScreen`'s `film.labour-verify` capability-hide) —
 *    there is nothing to hide behind here; a 403 from a deny-all profile surfaces as an ordinary
 *    load/action error instead.
 * 5. **Preview content renders as plain text, not `dangerouslySetInnerHTML`.** The v1 assembler
 *    documents emitting deterministic HTML/CSV text for non-form components; no other screen in
 *    this codebase renders raw server HTML, and introducing that pattern here would need its own
 *    security review, not a frontend-only call. The preview shows the real returned text verbatim
 *    (tags visible as text if present) rather than risk an unreviewed injection surface.
 */

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/** Component 5 (Technical/Creative Summary) — the ONE `source` this v1 backend never really
 * generates (`DossierComponentStub`, always `pending`, by design). Keying off `source` (the
 * assembly engine's own generic dispatch field) rather than `kind` keeps this config-driven, not a
 * hardcoded film-domain assumption. */
function isDeclaredStub(component: DossierComponentView): boolean {
  return component.source === 'approved_version'
}

function canPreview(component: DossierComponentView): boolean {
  return !isDeclaredStub(component) && (component.status === 'generated' || component.status === 'stale')
}

function isEligibleToFinalize(components: DossierComponentView[]): boolean {
  return components.length > 0 && components.every((c) => c.status === 'generated' || isDeclaredStub(c))
}

const STATUS_PILL: Record<string, { variant: StatusPillVariant; label: string }> = {
  generated: { variant: 'success', label: 'Generated' },
  pending: { variant: 'neutral', label: 'Pending' },
  stale: { variant: 'warning', label: 'Stale' },
  // Defensive only -- `dossier_component.status` (V22 CHECK) never actually returns "failed"; a
  // generator's hard failure aborts the whole assembly BackgroundJob instead of marking one
  // component failed (see the DossierJobHandle doc comment). Kept so an honest StatusPill still
  // renders if a future backend change ever adds this value, rather than falling through silently.
  failed: { variant: 'danger', label: 'Failed' },
}

function componentStatusPill(component: DossierComponentView): { variant: StatusPillVariant; label: string } {
  if (isDeclaredStub(component)) {
    return { variant: 'muted', label: 'Stub — not generated' }
  }
  return STATUS_PILL[component.status] ?? { variant: 'neutral', label: component.status }
}

function componentDetail(component: DossierComponentView): string {
  if (isDeclaredStub(component)) {
    return 'Stub — technical/creative narrative synthesis is not yet built for this cartridge; the manifest slot is reserved.'
  }
  if (component.status === 'generated') {
    return component.generatedAt ? `Generated ${formatDate(component.generatedAt)}.` : 'Generated.'
  }
  if (component.status === 'stale') {
    return 'Stale — a source this component depends on has changed since it was last generated. Regenerate to refresh it.'
  }
  return 'Not yet generated — Generate may still leave this blocked if its upstream data is not available yet.'
}

function downloadZip(base64: string, filename: string): void {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  const blob = new Blob([bytes], { type: 'application/zip' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

type Phase = 'resolving' | 'empty' | 'creating' | 'error' | 'ready'
type BusyAction = 'generate' | 'finalize' | 'download' | null

/** Picks the Dossier `GET /cases/{caseId}/dossiers` (newest-version-first) drives the checklist
 * for: the open `draft` if one exists (there is at most one per case+kind, AC1), else the newest
 * version (a `final`) — so re-visiting a fully-final case still shows its latest artifact/Download
 * rather than nothing. Returns `null` only for a genuinely empty list. */
function pickActive(list: DossierSummaryView[]): DossierSummaryView | null {
  return list.find((d) => d.status === 'draft') ?? list[0] ?? null
}

export interface DossierPanelProps {
  caseView: CaseView
}

/** Case · Legal entity · Fiscal year context strip (CLAUDE.md §4 IA) — `CaseView` itself carries
 * only raw ids, so names are resolved the same batched, client-side way `useCaseContexts`
 * (`screens/tasks/`) already does for the My Work / Task Board cross-case lists: one `GET
 * /cases/{id}` (already in hand here as `caseView`, but `useCaseContexts` re-derives its own
 * Account/Production context from it) plus `GET /legal-entities/{id}`/`GET /fiscal-years/{id}`. A
 * failed/absent lookup degrades to `EntityRef`'s own generic "<Kind> <shortId>" fallback — never a
 * fabricated name, never the bare full UUID as the headline. */
function ContextHeader({ caseView, dossier }: { caseView: CaseView; dossier: DossierView | null }) {
  const ctx = useCaseContexts([caseView.id]).get(caseView.id)
  return (
    <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <EntityRef kind="case" id={caseView.id} name={ctx?.caseLabel} />
      </span>
      <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <EntityRef kind="legalEntity" id={caseView.legalEntityId} name={ctx?.legalEntityName} />
      </span>
      <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        {caseView.fiscalYearId ? (
          <EntityRef kind="fiscalYear" id={caseView.fiscalYearId} name={ctx?.fiscalYearLabel} />
        ) : (
          'FY not set'
        )}
      </span>
      {dossier ? (
        <StatusPill variant={dossier.status === 'final' ? 'success' : 'neutral'} label={dossier.status === 'final' ? `FINAL v${dossier.version}` : 'DRAFT'} />
      ) : null}
    </div>
  )
}

function ActionButton({
  label,
  busyLabel,
  icon,
  primary,
  disabled,
  reasonId,
  reason,
  onClick,
}: {
  label: string
  busyLabel: string | null
  icon: IconName
  primary?: boolean
  disabled: boolean
  reasonId: string
  reason: string
  onClick: () => void
}) {
  return (
    <>
      <button
        type="button"
        className={`iw-btn${primary ? ' iw-btn--primary' : ''}`}
        aria-disabled={disabled}
        aria-describedby={reasonId}
        onClick={onClick}
      >
        <Icon name={icon} size={14} /> {busyLabel ?? label}
      </button>
      <span id={reasonId} className="iw-visually-hidden">
        {reason}
      </span>
    </>
  )
}

export function DossierPanel({ caseView }: DossierPanelProps) {
  const caseId = caseView.id
  const [phase, setPhase] = useState<Phase>('resolving')
  const [dossierList, setDossierList] = useState<DossierSummaryView[]>([])
  const [dossier, setDossier] = useState<DossierView | null>(null)
  const [components, setComponents] = useState<DossierComponentView[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<BusyAction>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [liveMessage, setLiveMessage] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [previewById, setPreviewById] = useState<
    Record<string, DossierComponentPreviewView | 'loading' | 'error'>
  >({})
  const [downloadingVersionId, setDownloadingVersionId] = useState<string | null>(null)

  const announce = useCallback((message: string) => setLiveMessage(message), [])

  const loadChecklist = useCallback(async (dossierId: string) => {
    const [d, comps] = await Promise.all([getDossier(dossierId), listDossierComponents(dossierId)])
    setDossier(d)
    setComponents([...comps].sort((a, b) => a.order - b.order))
    return d
  }, [])

  /** `GET /cases/{caseId}/dossiers` is the sole source of truth this screen resolves "the"
   * dossier from (replaces the old sessionStorage cache — see the file javadoc, gap #1). Loads the
   * active entry's full checklist alongside it so the existing Generate/Finalize/Download flow
   * below needs no further change. `preferredId` lets a just-created/just-acted-on Dossier win over
   * {@link pickActive}'s own draft-else-newest default (e.g. right after `createDossier` resolves,
   * before this same list read would otherwise see it). */
  const loadAll = useCallback(
    async (preferredId?: string) => {
      const list = await listCaseDossiers(caseId)
      const sorted = [...list].sort((a, b) => b.version - a.version)
      setDossierList(sorted)
      // preferredId (a just-created/just-acted-on Dossier this screen already knows the real id of)
      // always wins over re-deriving from `sorted` -- the real backend's list read is always
      // read-your-own-write consistent (same Postgres transaction boundary), so this only matters
      // defensively, never masks a genuine inconsistency.
      const activeId = preferredId ?? pickActive(sorted)?.id ?? null
      if (activeId) {
        await loadChecklist(activeId)
      } else {
        setDossier(null)
        setComponents([])
      }
      return sorted
    },
    [caseId, loadChecklist],
  )

  useEffect(() => {
    let cancelled = false
    setPhase('resolving')
    setLoadError(null)
    loadAll()
      .then((sorted) => {
        if (cancelled) return
        setPhase(sorted.length === 0 ? 'empty' : 'ready')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLoadError(errorMessage(err, "Could not load this case's dossiers."))
        setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [caseId, loadAll])

  async function handleStartDossier() {
    setPhase('creating')
    setLoadError(null)
    try {
      const created = await createDossier(caseId, { kind: 'client_deliverable', region: null })
      await loadAll(created.id)
      setPhase('ready')
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Race: another caller opened a draft since this screen last listed. The list is now the
        // SOLE source of truth -- re-fetch it and pick up that draft, rather than the old
        // sessionStorage-era 'createConflict' dead-end.
        try {
          const sorted = await loadAll()
          setPhase(sorted.length === 0 ? 'empty' : 'ready')
        } catch (listErr) {
          setLoadError(errorMessage(listErr, "Could not load this case's dossiers."))
          setPhase('error')
        }
        return
      }
      setLoadError(errorMessage(err, 'Could not start a dossier for this case.'))
      setPhase('error')
    }
  }

  function handleRetryLoad() {
    setPhase('resolving')
    setLoadError(null)
    loadAll()
      .then((sorted) => setPhase(sorted.length === 0 ? 'empty' : 'ready'))
      .catch((err: unknown) => {
        setLoadError(errorMessage(err, "Could not load this case's dossiers."))
        setPhase('error')
      })
  }

  async function handleDownloadVersion(entry: DossierSummaryView) {
    if (entry.status !== 'final' || downloadingVersionId) return
    setDownloadingVersionId(entry.id)
    setActionError(null)
    try {
      const artifact = await getDossierArtifact(entry.id)
      downloadZip(artifact.zipBase64, `dossier-${entry.caseId}-v${entry.version}.zip`)
      announce(`Version ${entry.version} download started.`)
    } catch (err) {
      const msg = errorMessage(err, `Could not download version ${entry.version}.`)
      setActionError(msg)
      announce(msg)
    } finally {
      setDownloadingVersionId(null)
    }
  }

  async function handleGenerate() {
    if (!dossier) return
    if (dossier.status === 'final') {
      announce('Final — immutable; regenerating would create a new dossier version, not built by this screen.')
      return
    }
    if (busyAction) {
      announce('Please wait for the current action to finish.')
      return
    }
    setBusyAction('generate')
    setActionError(null)
    try {
      const handle = await generateDossier(dossier.id)
      await loadAll(dossier.id)
      if (handle.status === 'failed') {
        const msg = `Dossier assembly failed (job ${handle.jobId}). Nothing was changed — try again, or contact support if this keeps happening.`
        setActionError(msg)
        announce(msg)
      } else {
        setActionError(null)
        announce('Dossier components regenerated.')
      }
    } catch (err) {
      const msg = errorMessage(err, 'Could not start dossier assembly.')
      setActionError(msg)
      announce(msg)
    } finally {
      setBusyAction(null)
    }
  }

  async function handleFinalize() {
    if (!dossier) return
    if (dossier.status === 'final') {
      announce('This dossier is already final.')
      return
    }
    if (!isEligibleToFinalize(components)) {
      announce('Blocked — resolve pending or stale components first.')
      return
    }
    if (busyAction) {
      announce('Please wait for the current action to finish.')
      return
    }
    setBusyAction('finalize')
    setActionError(null)
    try {
      const handle = await finalizeDossier(dossier.id)
      await loadAll(dossier.id)
      if (handle.status === 'failed') {
        const msg = `Dossier finalize failed (job ${handle.jobId}). The dossier is still draft — try again.`
        setActionError(msg)
        announce(msg)
      } else if (handle.status === 'already_final') {
        announce('This dossier was already final.')
      } else {
        setActionError(null)
        announce('Dossier finalized. Download is now available.')
      }
    } catch (err) {
      const msg = errorMessage(err, 'Could not finalize this dossier.')
      setActionError(msg)
      announce(msg)
    } finally {
      setBusyAction(null)
    }
  }

  async function handleDownload() {
    if (!dossier) return
    if (dossier.status !== 'final') {
      announce('Available once final.')
      return
    }
    if (busyAction) {
      announce('Please wait for the current action to finish.')
      return
    }
    setBusyAction('download')
    try {
      const artifact = await getDossierArtifact(dossier.id)
      downloadZip(artifact.zipBase64, `dossier-${dossier.caseId}-v${dossier.version}.zip`)
      announce('Dossier download started.')
    } catch (err) {
      const msg = errorMessage(err, 'Could not download the dossier artifact.')
      setActionError(msg)
      announce(msg)
    } finally {
      setBusyAction(null)
    }
  }

  async function handlePreviewToggle(component: DossierComponentView) {
    if (!dossier) return
    if (expandedId === component.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(component.id)
    if (previewById[component.id]) return
    setPreviewById((prev) => ({ ...prev, [component.id]: 'loading' }))
    try {
      const preview = await previewDossierComponent(dossier.id, component.id)
      setPreviewById((prev) => ({ ...prev, [component.id]: preview }))
    } catch {
      setPreviewById((prev) => ({ ...prev, [component.id]: 'error' }))
    }
  }

  const liveRegion = (
    <div aria-live="polite" role="status" className="iw-visually-hidden">
      {liveMessage}
    </div>
  )

  if (phase === 'resolving' || phase === 'creating') {
    return (
      <div>
        {liveRegion}
        <p style={{ color: 'var(--iw-text-muted)' }}>
          {phase === 'creating' ? 'Starting a dossier for this case…' : 'Loading dossier…'}
        </p>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div>
        {liveRegion}
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleRetryLoad()}>
              Try again
            </button>
          }
        >
          {loadError}
        </InlineAlert>
      </div>
    )
  }

  if (phase === 'empty' || !dossier) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
        {liveRegion}
        <ContextHeader caseView={caseView} dossier={null} />
        <p style={{ color: 'var(--iw-text-muted)' }}>No dossier has been started for this case yet.</p>
        <button type="button" className="iw-btn iw-btn--primary" style={{ alignSelf: 'flex-start' }} onClick={() => void handleStartDossier()}>
          <Icon name="archive" size={14} /> Start dossier
        </button>
      </div>
    )
  }

  // Every OTHER version besides the one currently driving the checklist below (AC10: priors are
  // retained, never deleted, when a new final is cut) -- a lightweight version-history strip, each
  // row's own Download for the ones that are `final`.
  const priorVersions = dossierList.filter((v) => v.id !== dossier.id)
  const eligible = isEligibleToFinalize(components)
  const generateDisabled = dossier.status === 'final' || busyAction !== null
  const finalizeDisabled = dossier.status === 'final' || !eligible || busyAction !== null
  const downloadDisabled = dossier.status !== 'final' || busyAction !== null

  const generateReason = dossier.status === 'final'
    ? 'Final — immutable; regenerating would create a new dossier version, not built by this screen.'
    : busyAction === 'generate'
      ? 'Dossier assembly is running…'
      : 'Regenerates every pending/stale component the current inputs allow.'
  const finalizeReason = dossier.status === 'final'
    ? 'Already final.'
    : eligible
      ? 'Ready to finalize.'
      : 'Blocked — resolve pending or stale components first.'
  const downloadReason = dossier.status === 'final'
    ? 'Final artifact ready — hashed, snapshot-pinned.'
    : 'Available once final.'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      {liveRegion}
      <ContextHeader caseView={caseView} dossier={dossier} />

      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        <ActionButton
          label="Generate"
          busyLabel={busyAction === 'generate' ? 'Generating…' : null}
          icon="refresh-cw"
          primary
          disabled={generateDisabled}
          reasonId="dossier-generate-reason"
          reason={generateReason}
          onClick={() => void handleGenerate()}
        />
        <ActionButton
          label="Finalize"
          busyLabel={busyAction === 'finalize' ? 'Finalizing…' : null}
          icon="lock"
          disabled={finalizeDisabled}
          reasonId="dossier-finalize-reason"
          reason={finalizeReason}
          onClick={() => void handleFinalize()}
        />
        <ActionButton
          label="Download"
          busyLabel={busyAction === 'download' ? 'Downloading…' : null}
          icon="download"
          disabled={downloadDisabled}
          reasonId="dossier-download-reason"
          reason={downloadReason}
          onClick={() => void handleDownload()}
        />
        {dossier.status === 'final' ? (
          <ActionButton
            label="Start new version"
            busyLabel={null}
            icon="archive"
            disabled={busyAction !== null}
            reasonId="dossier-start-new-version-reason"
            reason={`Cuts a new draft version (v${dossier.version + 1}) for this case, retaining every prior final.`}
            onClick={() => void handleStartDossier()}
          />
        ) : null}
      </div>

      {priorVersions.length > 0 ? (
        <div>
          <h3 style={{ fontSize: 'var(--iw-size-sm)', fontWeight: 500, margin: '0 0 var(--iw-space-1)' }}>
            Prior versions
          </h3>
          <table className="iw-table" aria-label="Prior dossier versions">
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Status</th>
                <th scope="col">Components</th>
                <th scope="col">Finalized</th>
                <th scope="col">Download</th>
              </tr>
            </thead>
            <tbody>
              {priorVersions.map((entry) => (
                <tr key={entry.id}>
                  <td>v{entry.version}</td>
                  <td>
                    <StatusPill
                      variant={entry.status === 'final' ? 'success' : 'neutral'}
                      label={entry.status === 'final' ? 'Final' : 'Draft'}
                    />
                  </td>
                  <td>{entry.componentCount}</td>
                  <td>{entry.finalizedAt ? formatDate(entry.finalizedAt) : '—'}</td>
                  <td>
                    {entry.status === 'final' ? (
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        aria-disabled={downloadingVersionId !== null}
                        aria-label={`Download version ${entry.version}`}
                        onClick={() => void handleDownloadVersion(entry)}
                      >
                        <Icon name="download" size={12} />{' '}
                        {downloadingVersionId === entry.id ? 'Downloading…' : 'Download'}
                      </button>
                    ) : (
                      <span style={{ color: 'var(--iw-text-subtle)' }}>Not final yet</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <table className="iw-table" aria-label="Dossier components">
        <thead>
          <tr>
            <th scope="col">Component</th>
            <th scope="col">Status</th>
            <th scope="col">Detail</th>
            <th scope="col">Preview</th>
          </tr>
        </thead>
        <tbody>
          {components.map((component) => {
            const pill = componentStatusPill(component)
            const previewable = canPreview(component)
            const expanded = expandedId === component.id
            const preview = previewById[component.id]
            return (
              <Fragment key={component.id}>
                <tr>
                  <td>{component.title}</td>
                  <td>
                    <StatusPill variant={pill.variant} label={pill.label} />
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {componentDetail(component)}
                  </td>
                  <td>
                    {previewable ? (
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        onClick={() => void handlePreviewToggle(component)}
                        aria-expanded={expanded}
                        aria-label={`Preview ${component.title}`}
                      >
                        {expanded ? 'Hide preview' : 'Preview'}
                      </button>
                    ) : (
                      <span style={{ color: 'var(--iw-text-subtle)' }}>No content yet</span>
                    )}
                  </td>
                </tr>
                {expanded ? (
                  <tr>
                    <td colSpan={4}>
                      {preview === 'loading' || preview === undefined ? (
                        <p style={{ color: 'var(--iw-text-muted)' }}>Loading preview…</p>
                      ) : preview === 'error' ? (
                        <InlineAlert severity="error">Could not load this preview.</InlineAlert>
                      ) : (
                        <pre
                          style={{
                            whiteSpace: 'pre-wrap',
                            background: 'var(--iw-neutral-100)',
                            borderRadius: 'var(--iw-radius)',
                            padding: 'var(--iw-space-3)',
                            margin: 0,
                            fontFamily: 'inherit',
                            fontSize: 'var(--iw-size-xs)',
                          }}
                        >
                          {preview.content}
                        </pre>
                      )}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
